import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  ChevronRight, ChevronLeft, Loader2, Dumbbell, Target, Flame, Trophy, Check, SkipForward,
} from "lucide-react";
import BrandMark from "../components/BrandMark";

const EQUIPMENT = ["bodyweight", "dumbbells", "barbell", "machines", "kettlebell", "resistance bands", "pull-up bar"];
const GOAL_OPTIONS = [
  { value: "lose_fat", label: "Lose fat", desc: "Drop body fat while keeping strength" },
  { value: "build_muscle", label: "Build muscle", desc: "Hypertrophy & lean mass" },
  { value: "maintain", label: "Maintain", desc: "Stay where you are, get sharper" },
  { value: "endurance", label: "Endurance", desc: "Stamina, cardio, conditioning" },
];
const LEVELS = [
  { value: "beginner", label: "Beginner", desc: "< 6 months training" },
  { value: "intermediate", label: "Intermediate", desc: "6 months – 2 years" },
  { value: "advanced", label: "Advanced", desc: "2+ years consistent" },
];

const STEPS = [
  { id: 1, title: "Basics", icon: Dumbbell },
  { id: 2, title: "Training", icon: Target },
  { id: 3, title: "First goal", icon: Flame },
  { id: 4, title: "Done", icon: Trophy },
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user, setUser, refreshUser } = useAuth();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);

  // Step 1
  const [age, setAge] = useState(user?.age || "");
  const [sex, setSex] = useState(user?.sex || "");
  const [heightCm, setHeightCm] = useState(user?.height_cm || "");
  const [weightKg, setWeightKg] = useState(user?.weight_kg || "");

  // Step 2
  const [goal, setGoal] = useState(user?.goal || "");
  const [fitnessLevel, setFitnessLevel] = useState(user?.fitness_level || "");
  const [daysPerWeek, setDaysPerWeek] = useState(user?.days_per_week || 3);
  const [equipment, setEquipment] = useState(user?.equipment || ["bodyweight"]);

  // Step 3
  const [targetWeight, setTargetWeight] = useState("");
  const [targetDate, setTargetDate] = useState("");

  const totalSteps = STEPS.length;
  const progressPct = Math.round(((step - 1) / (totalSteps - 1)) * 100);

  const validateStep = () => {
    if (step === 1) {
      if (!age || !sex || !heightCm || !weightKg) {
        toast.error("Fill all fields to continue");
        return false;
      }
    }
    if (step === 2) {
      if (!goal || !fitnessLevel || equipment.length === 0) {
        toast.error("Pick goal, level and at least one equipment");
        return false;
      }
    }
    return true;
  };

  const persistBasics = async () => {
    const payload = {
      age: Number(age),
      sex,
      height_cm: Number(heightCm),
      weight_kg: Number(weightKg),
    };
    const { data } = await api.put("/auth/profile", payload);
    setUser(data);
    localStorage.setItem("if_user", JSON.stringify(data));
    // Also log weight so goals projections work
    try {
      await api.post("/weight/log", { weight_kg: Number(weightKg) });
    } catch (err) {
      // Non-blocking — weight log failure shouldn't stop onboarding.
      console.warn("Initial weight log failed:", err?.response?.data?.detail || err?.message);
    }
  };

  const persistTraining = async () => {
    const payload = {
      goal,
      fitness_level: fitnessLevel,
      days_per_week: Number(daysPerWeek),
      equipment,
    };
    const { data } = await api.put("/auth/profile", payload);
    setUser(data);
    localStorage.setItem("if_user", JSON.stringify(data));
  };

  const persistGoal = async () => {
    if (!targetWeight) return; // optional
    const payload = {
      type: "weight",
      target_weight_kg: Number(targetWeight),
    };
    if (targetDate) payload.target_date = targetDate;
    await api.post("/goals", payload);
  };

  const next = async () => {
    if (!validateStep()) return;
    setSaving(true);
    try {
      if (step === 1) await persistBasics();
      if (step === 2) await persistTraining();
      if (step === 3) {
        if (targetWeight) await persistGoal();
      }
      if (step < totalSteps) setStep(step + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const back = () => setStep((s) => Math.max(1, s - 1));

  const finish = async () => {
    try {
      await refreshUser();
    } catch (err) {
      // Non-fatal — dashboard will re-fetch user on mount.
      console.warn("refreshUser after onboarding failed:", err?.response?.data?.detail || err?.message);
    }
    navigate("/app/dashboard");
  };

  const skipAll = () => navigate("/app/dashboard");

  const toggleEq = (eq) =>
    setEquipment((cur) => (cur.includes(eq) ? cur.filter((e) => e !== eq) : [...cur, eq]));

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="onboarding-page">
      {/* top brand */}
      <div className="h-16 px-6 lg:px-10 flex items-center justify-between border-b border-white/10">
        <BrandMark size={32} />
        <button
          onClick={skipAll}
          className="text-xs uppercase tracking-widest text-white/40 hover:text-white inline-flex items-center gap-1.5"
          data-testid="onboarding-skip-all"
        >
          <SkipForward className="w-3.5 h-3.5" />
          Skip for now
        </button>
      </div>

      <div className="max-w-3xl mx-auto p-6 lg:p-10 fade-up">
        {/* progress */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-3">
            <div className="label-eyebrow text-[#6B9BFF]">Step {step} of {totalSteps}</div>
            <div className="text-xs text-white/50">{progressPct}% complete</div>
          </div>
          <div className="h-1 bg-white/10">
            <div
              className="h-full bg-[#6B9BFF] transition-all duration-500"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className="grid grid-cols-4 gap-2 mt-4">
            {STEPS.map((s) => {
              const Icon = s.icon;
              const isActive = s.id === step;
              const isDone = s.id < step;
              return (
                <div key={s.id} className="flex items-center gap-2 text-xs uppercase tracking-wider">
                  <div className={`w-6 h-6 flex items-center justify-center border ${
                    isActive ? "border-[#6B9BFF] text-[#6B9BFF]" : isDone ? "border-[#6B9BFF]/40 bg-[#6B9BFF]/10 text-[#6B9BFF]" : "border-white/10 text-white/40"
                  }`}>
                    {isDone ? <Check className="w-3 h-3" /> : <Icon className="w-3 h-3" />}
                  </div>
                  <span className={isActive ? "text-white" : isDone ? "text-white/60" : "text-white/30"}>
                    {s.title}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* STEP 1 */}
        {step === 1 && (
          <section data-testid="onboarding-step-1">
            <div className="label-eyebrow text-[#6B9BFF] mb-2">Welcome to DAIONE</div>
            <h1 className="font-display text-4xl sm:text-5xl leading-none mb-3">
              Let's get to know you.
            </h1>
            <p className="text-white/60 mb-8 max-w-lg">
              We need a few stats to calculate your TDEE and tailor AI workouts. Takes 60 seconds.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label-eyebrow text-white/50 block mb-2">Age</label>
                <input
                  type="number" min="10" max="100"
                  value={age} onChange={(e) => setAge(e.target.value)}
                  placeholder="28"
                  className="w-full bg-[#121212] border border-white/10 px-4 py-3 focus:outline-none focus:border-[#6B9BFF]"
                  data-testid="onboarding-age"
                />
              </div>
              <div>
                <label className="label-eyebrow text-white/50 block mb-2">Sex</label>
                <select
                  value={sex} onChange={(e) => setSex(e.target.value)}
                  className="w-full bg-[#121212] border border-white/10 px-4 py-3 focus:outline-none focus:border-[#6B9BFF]"
                  data-testid="onboarding-sex"
                >
                  <option value="">Select</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="label-eyebrow text-white/50 block mb-2">Height (cm)</label>
                <input
                  type="number" step="0.1"
                  value={heightCm} onChange={(e) => setHeightCm(e.target.value)}
                  placeholder="178"
                  className="w-full bg-[#121212] border border-white/10 px-4 py-3 focus:outline-none focus:border-[#6B9BFF]"
                  data-testid="onboarding-height"
                />
              </div>
              <div>
                <label className="label-eyebrow text-white/50 block mb-2">Weight (kg)</label>
                <input
                  type="number" step="0.1"
                  value={weightKg} onChange={(e) => setWeightKg(e.target.value)}
                  placeholder="74.5"
                  className="w-full bg-[#121212] border border-white/10 px-4 py-3 focus:outline-none focus:border-[#6B9BFF]"
                  data-testid="onboarding-weight"
                />
              </div>
            </div>
          </section>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <section data-testid="onboarding-step-2">
            <div className="label-eyebrow text-[#6B9BFF] mb-2">Training preferences</div>
            <h1 className="font-display text-4xl sm:text-5xl leading-none mb-3">
              How do you train?
            </h1>
            <p className="text-white/60 mb-8 max-w-lg">
              Drives every AI plan we generate for you. Change it anytime in Profile.
            </p>

            <div className="mb-6">
              <div className="label-eyebrow text-white/50 mb-3">Primary goal</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {GOAL_OPTIONS.map((g) => (
                  <button
                    type="button"
                    key={g.value}
                    onClick={() => setGoal(g.value)}
                    className={`text-left p-4 border transition-colors ${
                      goal === g.value ? "border-[#6B9BFF] bg-[#6B9BFF]/5" : "border-white/10 hover:border-white/30"
                    }`}
                    data-testid={`onboarding-goal-${g.value}`}
                  >
                    <div className="font-display text-lg">{g.label}</div>
                    <div className="text-xs text-white/50 mt-1">{g.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <div className="label-eyebrow text-white/50 mb-3">Fitness level</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {LEVELS.map((l) => (
                  <button
                    type="button"
                    key={l.value}
                    onClick={() => setFitnessLevel(l.value)}
                    className={`text-left p-4 border transition-colors ${
                      fitnessLevel === l.value ? "border-[#6B9BFF] bg-[#6B9BFF]/5" : "border-white/10 hover:border-white/30"
                    }`}
                    data-testid={`onboarding-level-${l.value}`}
                  >
                    <div className="font-display text-base">{l.label}</div>
                    <div className="text-xs text-white/50 mt-1">{l.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <div className="label-eyebrow text-white/50">Days per week</div>
                <div className="font-display text-2xl text-[#6B9BFF]" data-testid="onboarding-days-value">{daysPerWeek}</div>
              </div>
              <input
                type="range" min="1" max="7" value={daysPerWeek}
                onChange={(e) => setDaysPerWeek(Number(e.target.value))}
                className="w-full accent-[#6B9BFF]"
                data-testid="onboarding-days"
              />
            </div>

            <div>
              <div className="label-eyebrow text-white/50 mb-3">Available equipment</div>
              <div className="flex flex-wrap gap-2">
                {EQUIPMENT.map((eq) => (
                  <button
                    type="button"
                    key={eq}
                    onClick={() => toggleEq(eq)}
                    className={`px-3 py-2 text-xs uppercase tracking-wider border transition-colors ${
                      equipment.includes(eq)
                        ? "border-[#6B9BFF] text-[#6B9BFF] bg-[#6B9BFF]/5"
                        : "border-white/10 text-white/60 hover:border-white/30"
                    }`}
                    data-testid={`onboarding-equip-${eq.replace(/\s/g, "-")}`}
                  >
                    {eq}
                  </button>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <section data-testid="onboarding-step-3">
            <div className="label-eyebrow text-[#6B9BFF] mb-2">Set your first goal</div>
            <h1 className="font-display text-4xl sm:text-5xl leading-none mb-3">
              What weight are you aiming for?
            </h1>
            <p className="text-white/60 mb-8 max-w-lg">
              We'll calculate the timeline and your daily calorie target. Skip if you're not sure — you can always add goals later.
            </p>

            <div className="border border-white/10 bg-[#121212] p-6">
              <div className="text-sm text-white/60 mb-1">Current weight</div>
              <div className="font-display text-3xl mb-6">{weightKg ? `${weightKg} kg` : "—"}</div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label-eyebrow text-white/50 block mb-2">Target weight (kg)</label>
                  <input
                    type="number" step="0.1"
                    value={targetWeight} onChange={(e) => setTargetWeight(e.target.value)}
                    placeholder="e.g. 78"
                    className="w-full bg-[#0a0a0a] border border-white/10 px-4 py-3 focus:outline-none focus:border-[#6B9BFF]"
                    data-testid="onboarding-target-weight"
                  />
                </div>
                <div>
                  <label className="label-eyebrow text-white/50 block mb-2">By when? (optional)</label>
                  <input
                    type="date"
                    value={targetDate} onChange={(e) => setTargetDate(e.target.value)}
                    className="w-full bg-[#0a0a0a] border border-white/10 px-4 py-3 focus:outline-none focus:border-[#6B9BFF]"
                    data-testid="onboarding-target-date"
                  />
                </div>
              </div>

              <div className="mt-5 text-xs text-white/40">
                Safe pace is capped at 1 kg/week. We'll auto-set a sensible pace if you skip the date.
              </div>
            </div>
          </section>
        )}

        {/* STEP 4 */}
        {step === 4 && (
          <section data-testid="onboarding-step-4" className="text-center py-10">
            <div className="w-20 h-20 mx-auto bg-[#6B9BFF] flex items-center justify-center mb-6">
              <Trophy className="w-10 h-10 text-black" />
            </div>
            <div className="label-eyebrow text-[#6B9BFF] mb-2">You're set</div>
            <h1 className="font-display text-4xl sm:text-5xl leading-none mb-4">
              Time to get to work.
            </h1>
            <p className="text-white/60 mb-10 max-w-md mx-auto">
              Your dashboard now shows your TDEE-adjusted calorie target. Next, generate your first AI workout.
            </p>
            <button
              onClick={finish}
              className="bg-[#6B9BFF] text-black font-black uppercase tracking-widest px-8 py-4 hover:bg-white transition-colors inline-flex items-center gap-2"
              data-testid="onboarding-finish"
            >
              Enter dashboard <ChevronRight className="w-4 h-4" />
            </button>
          </section>
        )}

        {/* Navigation */}
        {step < 4 && (
          <div className="mt-10 flex items-center justify-between gap-4">
            <button
              onClick={back}
              disabled={step === 1 || saving}
              className="text-white/60 hover:text-white text-sm uppercase tracking-widest font-bold inline-flex items-center gap-1.5 disabled:opacity-30"
              data-testid="onboarding-back"
            >
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <div className="flex items-center gap-3">
              {step === 3 && (
                <button
                  onClick={async () => {
                    setTargetWeight("");
                    setTargetDate("");
                    setSaving(true);
                    try {
                      setStep(4);
                    } finally {
                      setSaving(false);
                    }
                  }}
                  className="text-white/40 hover:text-white text-xs uppercase tracking-widest"
                  data-testid="onboarding-skip-goal"
                >
                  Skip goal
                </button>
              )}
              <button
                onClick={next}
                disabled={saving}
                className="bg-[#6B9BFF] text-black font-black uppercase tracking-widest px-6 py-3 hover:bg-white transition-colors inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="onboarding-next"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (step === 3 ? "Finish" : "Continue")}
                {!saving && <ChevronRight className="w-4 h-4" />}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
